import { useEffect, useState } from "react";

import Button from "@shared/v2/Button";
import { ArrowRightSolidV6 } from "@shared/v2/Icomoon";
import Modal from "@shared/v2/Modal";

import DetailsPanel from "./panels/DetailsPanel";
import IntroPanel from "./panels/IntroPanel";
import LivePanel from "./panels/LivePanel";
import SyncingPanel from "./panels/SyncingPanel";
import panelClassName from "./panelClassName";
import { useActivateConnectService } from "./useActivateConnectService";
import useBrand from "./useBrand";

const INTRO = 1;
const DETAILS = 2;
const SYNCING = 3;
const LIVE = 4;

// Step 3 is a real step, not a spinner: it holds for at least this long even if
// the POST returns instantly, so the transition reads as a deliberate stage
// rather than a flash.
const DWELL_MS = 700;

const delay = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const FOOTER_BASE = "tw-mt-[32px] tw-flex tw-items-center tw-px-[32px] tw-pb-[32px]";

interface ConnectServicesActivationModalProps {
  show?: boolean;
  onCancel?: () => void;
  onActivated?: () => void;
}

const ConnectServicesActivationModal = ({
  show = false,
  onCancel = () => {},
  onActivated = () => {},
}: ConnectServicesActivationModalProps) => {
  const [step, setStep] = useState(INTRO);
  const { activate, error, didActivate, resetError } = useActivateConnectService();

  const { name: brandName, hero: heroImage } = useBrand();

  const isSyncing = step === SYNCING;
  const isLive = step === LIVE;

  // Reset on open, never on close: the v2 Modal runs a 400ms exit animation with
  // its children still mounted, so resetting on close would snap the body back to
  // step 1 in full view while the modal fades out. The error is reset here too —
  // otherwise a failed attempt's message survives a close/reopen cycle, since the
  // call site keeps this component mounted (with show=false) rather than
  // unmounting it, so the hook's state would never naturally clear.
  useEffect(() => {
    if (show) {
      setStep(INTRO);
      resetError();
    }
  }, [show, resetError]);

  const handleClose = () => {
    // Step 3 is uninterruptible — a mid-flight close would strand the request.
    if (isSyncing) return;

    // Deferred to close on purpose: the call site renders this modal only while
    // `connectService` is false, so patching the flag any earlier unmounts the
    // modal and step 4 never renders.
    if (didActivate) onActivated();

    onCancel();
  };

  const handleEnable = async () => {
    setStep(SYNCING);

    const [succeeded] = await Promise.all([activate(), delay(DWELL_MS)]);

    setStep(succeeded ? LIVE : DETAILS);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      contentSize="medium"
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-min-h-full tw-max-h-full"
      contentClassName="tw-m-auto tw-overflow-hidden !tw-p-0 !tw-shadow-[0px_1px_12px_5px_rgba(51,51,51,0.25)]"
    >
      <img
        src={heroImage}
        alt={`${brandName} and Connect Services`}
        className="tw-block tw-h-[168px] tw-w-full tw-object-cover"
      />

      <Modal.Body className="tw-relative tw-mt-[32px] tw-h-[328px] tw-overflow-hidden">
        <IntroPanel className={panelClassName(INTRO, step)} />
        <DetailsPanel className={panelClassName(DETAILS, step)} error={error} />
        <SyncingPanel className={panelClassName(SYNCING, step)} />
        <LivePanel className={panelClassName(LIVE, step)} />
      </Modal.Body>

      {/*
        HEIGHT INVARIANT: this footer renders on all four steps. Step 3 hides its
        buttons with `tw-invisible`, which preserves layout. Unmounting them or
        using `tw-hidden` collapses the footer and changes the modal's height —
        the one thing this flow must never do.
      */}
      <Modal.Footer
        data-cy="modal-footer"
        className={`${FOOTER_BASE} ${isLive ? "tw-justify-center" : "tw-justify-between"}`}
      >
        {isLive ? (
          <>
            {/* @ts-expect-error props */}
            <Button size="medium" schema="secondary" onClick={handleClose}>
              Got it!
            </Button>
          </>
        ) : (
          <>
            {/* @ts-expect-error props */}
            <Button
              size="medium"
              schema="tertiary"
              disabled={isSyncing}
              className={isSyncing ? "tw-invisible" : ""}
              onClick={handleClose}
            >
              Cancel
            </Button>
            {/* @ts-expect-error props */}
            <Button
              size="medium"
              schema="primary"
              disabled={isSyncing}
              className={isSyncing ? "tw-invisible" : ""}
              onClick={step === INTRO ? () => setStep(DETAILS) : handleEnable}
            >
              {step === INTRO ? (
                <>
                  Next
                  <ArrowRightSolidV6 size="m" className="tw-ml-[6px]" />
                </>
              ) : (
                "Enable now"
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ConnectServicesActivationModal;
